import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { X, Heart, Share2, Trash2, ChevronLeft, ChevronRight, Link2 } from "lucide-react";
import { BeadString } from "./BraceletProfileCard";
import parseCaption from "../utils/parseCaption";
import { T } from "../theme";

/**
 * PhotoViewerModal — the one photo lightbox.
 *
 * True full-screen on mobile (bottom nav and all chrome covered), centered
 * 680px glass card on desktop. Swipe left/right (or arrow keys / desktop
 * arrow buttons) to move through the current gallery without closing.
 *
 * Props:
 *   photos       – array of photo objects from any photos endpoint
 *   initialIndex – which photo to open on
 *   apiUrl       – API base for image srcs
 *   viewerId     – logged-in user id (enables liking); optional
 *   friends      – friend list for @mention userId resolution; optional
 *   onClose      – dismiss
 *   onDelete     – (photoId) => void; delete button shows only for own photos
 */


const SWIPE_THRESHOLD = 55;

function PhotoViewerModal({
  photos = [],
  initialIndex = 0,
  apiUrl = "",
  viewerId = null,
  friends = [],
  onClose,
  onDelete,
}) {
  const navigate = useNavigate();
  const [index, setIndex] = useState(
    Math.min(Math.max(initialIndex, 0), Math.max(photos.length - 1, 0))
  );
  // Like state per photo id — seeded from the photo objects, updated optimistically
  const [likeState, setLikeState] = useState({});
  const [toast, setToast]         = useState(null);
  const [zoom, setZoom]           = useState(1);

  const touchRef = useRef({ startX: 0, startY: 0, pinchDist: 0, pinching: false });
  const toastTimer = useRef(null);

  const photo = photos[index] || null;

  const goTo = useCallback((next) => {
    setZoom(1);
    setIndex((i) => {
      const n = typeof next === "function" ? next(i) : next;
      return Math.min(Math.max(n, 0), photos.length - 1);
    });
  }, [photos.length]);

  // Keyboard: arrows navigate, Escape closes
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft") goTo((i) => i - 1);
      else if (e.key === "ArrowRight") goTo((i) => i + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goTo, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  if (!photo) return null;

  const showToast = (msg) => {
    clearTimeout(toastTimer.current);
    setToast(msg);
    toastTimer.current = setTimeout(() => setToast(null), 2200);
  };

  const likes = likeState[photo.id] || {
    count: photo.likeCount || 0,
    liked: !!photo.likedByViewer,
  };

  const toggleLike = async () => {
    if (!viewerId) { showToast("Log in to send some PLUR 💖"); return; }
    const prev = likes;
    const optimistic = {
      count: prev.count + (prev.liked ? -1 : 1),
      liked: !prev.liked,
    };
    setLikeState((s) => ({ ...s, [photo.id]: optimistic }));
    try {
      const res = await axios.post(`${apiUrl}/api/photos/${photo.id}/like`, { userId: viewerId });
      if (res.data.success) {
        setLikeState((s) => ({ ...s, [photo.id]: { count: res.data.likeCount, liked: res.data.liked } }));
      }
    } catch (err) {
      console.error("Like error:", err);
      setLikeState((s) => ({ ...s, [photo.id]: prev }));
    }
  };

  const sharePhoto = async () => {
    const raw = photo.imageUrl || "";
    const link = raw.startsWith("http")
      ? raw
      : `${apiUrl || window.location.origin}${raw}`;
    try {
      await navigator.clipboard.writeText(link);
      showToast("Link copied ✨");
    } catch {
      // Clipboard API unavailable (http / older mobile) — legacy fallback
      const ta = document.createElement("textarea");
      ta.value = link;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        showToast("Link copied");
      } catch {
        showToast("Couldn't copy link");
      }
      document.body.removeChild(ta);
    }
  };

  // ── Touch: pinch-to-zoom (2 fingers) + swipe nav (1 finger) ────────────────
  const pinchDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.hypot(dx, dy);
  };

  const onTouchStart = (e) => {
    const t = touchRef.current;
    if (e.touches.length === 2) {
      t.pinching = true;
      t.pinchDist = pinchDistance(e.touches);
    } else if (e.touches.length === 1) {
      t.startX = e.touches[0].clientX;
      t.startY = e.touches[0].clientY;
    }
  };

  const onTouchMove = (e) => {
    const t = touchRef.current;
    if (t.pinching && e.touches.length === 2 && t.pinchDist > 0) {
      const scale = pinchDistance(e.touches) / t.pinchDist;
      setZoom(Math.min(Math.max(scale, 1), 4));
    }
  };

  const onTouchEnd = (e) => {
    const t = touchRef.current;
    if (t.pinching) {
      if (e.touches.length === 0) {
        t.pinching = false;
        setZoom(1); // snap back so swipe nav stays predictable
      }
      return;
    }
    if (e.changedTouches.length === 1) {
      const dx = e.changedTouches[0].clientX - t.startX;
      const dy = e.changedTouches[0].clientY - t.startY;
      if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy) * 1.4) {
        goTo((i) => (dx < 0 ? i + 1 : i - 1));
      }
    }
  };

  const isOwn = onDelete && viewerId && (!photo.userId || String(photo.userId) === String(viewerId));
  const kandi = photo.kandi || null;
  const kandiLinkId = kandi?.id || photo.kandiId;

  const actionBtn = (active) => ({
    display: "flex", alignItems: "center", gap: 7,
    padding: "10px 16px", minHeight: 42, borderRadius: 12,
    background: active ? "rgba(255,60,172,0.16)" : "rgba(255,255,255,0.05)",
    border: active ? "1px solid rgba(255,60,172,0.4)" : "1px solid rgba(255,255,255,0.1)",
    color: active ? T.pink : T.textPrimary,
    fontSize: "0.82rem", fontWeight: 700, cursor: "pointer",
    transition: "all 0.15s",
  });

  const arrowBtn = (side) => ({
    position: "absolute", top: "50%", [side]: 14, transform: "translateY(-50%)",
    width: 42, height: 42, borderRadius: "50%",
    background: "rgba(0,0,0,0.5)", border: "1px solid rgba(255,255,255,0.15)",
    color: "#fff", cursor: "pointer", zIndex: 5,
    display: "flex", alignItems: "center", justifyContent: "center",
    backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
  });

  return (
    <div
      className="pvm-overlay"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 300,
        background: "rgba(5,3,12,0.88)",
        backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      {/* Desktop prev/next arrows — hidden on phones (see .pvm-arrow media rule) */}
      {index > 0 && (
        <button
          className="pvm-arrow"
          aria-label="Previous photo"
          onClick={(e) => { e.stopPropagation(); goTo((i) => i - 1); }}
          style={arrowBtn("left")}
        >
          <ChevronLeft size={22} />
        </button>
      )}
      {index < photos.length - 1 && (
        <button
          className="pvm-arrow"
          aria-label="Next photo"
          onClick={(e) => { e.stopPropagation(); goTo((i) => i + 1); }}
          style={arrowBtn("right")}
        >
          <ChevronRight size={22} />
        </button>
      )}

      <div
        className="pvm-card"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          width: "100%", maxWidth: 680,
          maxHeight: "92vh",
          background: T.modalCard,
          border: T.border, borderRadius: 24,
          display: "flex", flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 24px 70px rgba(0,0,0,0.6)",
        }}
      >
        {/* ── Photo ── */}
        <div style={{ position: "relative", background: "rgba(0,0,0,0.5)", flexShrink: 0 }}>
          <img
            src={photo.imageUrl?.startsWith("http") ? photo.imageUrl : `${apiUrl}${photo.imageUrl}`}
            alt={photo.message || "Photo"}
            draggable={false}
            style={{
              display: "block", width: "100%", maxHeight: "55vh",
              objectFit: "contain",
              touchAction: "manipulation",
              transform: `scale(${zoom})`,
              transition: zoom === 1 ? "transform 0.25s ease" : "none",
              userSelect: "none",
            }}
          />
          <button
            aria-label="Close"
            onClick={onClose}
            style={{
              position: "absolute", top: 12, right: 12,
              width: 36, height: 36, borderRadius: "50%",
              background: "rgba(0,0,0,0.55)", border: "1px solid rgba(255,255,255,0.15)",
              color: "#fff", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              zIndex: 4,
            }}
          >
            <X size={18} />
          </button>
          {photos.length > 1 && (
            <span style={{
              position: "absolute", bottom: 10, left: "50%", transform: "translateX(-50%)",
              padding: "3px 12px", borderRadius: 20,
              background: "rgba(0,0,0,0.55)", color: "rgba(255,255,255,0.8)",
              fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.06em",
            }}>
              {index + 1} / {photos.length}
            </span>
          )}
        </div>

        {/* ── Caption + bracelet (scrolls if long) ── */}
        <div style={{ padding: "16px 20px 4px", overflowY: "auto", flex: 1, minHeight: 0 }}>
          {photo.message ? (
            <p style={{
              margin: 0, color: T.textPrimary, fontSize: "0.92rem", lineHeight: 1.55,
              overflowWrap: "break-word",
            }}>
              {parseCaption(photo.message, friends)}
            </p>
          ) : null}

          {(kandi || photo.kandiName) && (
            <div style={{
              marginTop: photo.message ? 14 : 0,
              padding: "12px 14px", borderRadius: 16,
              background: "rgba(180,79,255,0.08)",
              border: "1px solid rgba(180,79,255,0.25)",
            }}>
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 8, flexWrap: "wrap",
              }}>
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  color: T.purple, fontSize: "0.82rem", fontWeight: 700,
                }}>
                  <Link2 size={12} />
                  {kandi?.emoji ? `${kandi.emoji} ` : ""}{kandi?.name || photo.kandiName}
                </span>
                {kandiLinkId && (
                  <button
                    onClick={() => { onClose?.(); navigate(`/kandi/${kandiLinkId}`); }}
                    style={{
                      background: "none", border: "none", padding: "4px 0",
                      color: T.cyan, fontSize: "0.78rem", fontWeight: 700,
                      cursor: "pointer", textShadow: `0 0 8px ${T.cyan}44`,
                    }}
                  >
                    View Bracelet →
                  </button>
                )}
              </div>
              {kandi?.colors?.length > 0 && <BeadString colors={kandi.colors} shapes={kandi.shapes || []} size={20} />}
            </div>
          )}

          <p style={{ margin: "10px 0 0", color: T.textMuted, fontSize: "0.72rem" }}>
            {photo.createdAt && new Date(photo.createdAt).toLocaleDateString("en-US", {
              month: "short", day: "numeric", year: "numeric",
            })}
          </p>
        </div>

        {/* ── Actions ── */}
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "12px 20px calc(14px + env(safe-area-inset-bottom))",
          borderTop: "1px solid rgba(255,255,255,0.06)",
          flexShrink: 0,
        }}>
          <button onClick={toggleLike} style={actionBtn(likes.liked)} aria-label="Like photo">
            <Heart size={15} fill={likes.liked ? T.pink : "none"} />
            {likes.count > 0 ? likes.count : "Like"}
          </button>
          <button onClick={sharePhoto} style={actionBtn(false)} aria-label="Share photo">
            <Share2 size={15} /> Share
          </button>
          <div style={{ flex: 1 }} />
          {isOwn && (
            <button
              onClick={() => onDelete(photo.id)}
              aria-label="Delete photo"
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "10px 14px", minHeight: 42, borderRadius: 12,
                background: "rgba(255,68,68,0.12)", border: "1px solid rgba(255,68,68,0.3)",
                color: "#ff6b6b", fontSize: "0.78rem", fontWeight: 700, cursor: "pointer",
              }}
            >
              <Trash2 size={13} /> Delete
            </button>
          )}
        </div>
      </div>

      {/* ── Toast ── */}
      {toast && (
        <div
          className="pvm-toast"
          style={{
            position: "fixed", bottom: "calc(28px + env(safe-area-inset-bottom))",
            left: "50%", transform: "translateX(-50%)",
            padding: "10px 20px", borderRadius: 30, zIndex: 310,
            background: "rgba(16,8,32,0.95)",
            border: "1px solid rgba(0,245,255,0.35)",
            color: T.textPrimary, fontSize: "0.82rem", fontWeight: 700,
            boxShadow: "0 8px 28px rgba(0,0,0,0.5), 0 0 18px rgba(0,245,255,0.15)",
            whiteSpace: "nowrap",
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

export default PhotoViewerModal;
